import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { setCriteria, setInputValue, updateWellInputByUnit } from "../../store/slices/vlpCalculatorSlice";
import { setSecondWellIsOpen, getSecondWellIsOpen } from "../../store/slices/appSlice";
import { useTranslation } from "react-i18next";
import { InputRow } from "./InputRow";
import type { IDDItem } from "../../components/common/DropdownMenu";
import { UnitConverter } from "../../components/MeasurementUnits/UnitConverter";
import DropdownMenu from "../../components/common/DropdownMenu";
import type { VLPCriteria } from "../../interfaces/interfaces";

interface IProps {
	wellId: "well1" | "well2";
}

const converter = new UnitConverter();

export const WellInputs = ({ wellId }: IProps) => {
	const calculator = useAppSelector(state => state.vlpCalculator);
	const dispatch = useAppDispatch();
	const secondWellOnScreen = useAppSelector(getSecondWellIsOpen);
	const { t } = useTranslation();

	const toggleSecondWell = () => {
		dispatch(setSecondWellIsOpen(!secondWellOnScreen));
	}

	const onInputChange = useCallback(
		(inputName: string, value: number | null) => {
			dispatch(
				setInputValue({ wellId: wellId, inputName: inputName, value: value })
			);
		},
		[dispatch, calculator]
	);

	const onSelectUnit = useCallback((inputId: string, selectedUnitId: string) => {
		dispatch(
			updateWellInputByUnit({
				wellId: wellId,
				inputId: inputId,
				selectedUnitId: selectedUnitId,
			})
		);
	}, [dispatch, calculator]);

	const inputs = useMemo(() => {
		if (wellId === "well1") {
			return calculator.well1.inputs;
		} else if (wellId === "well2") {
			return calculator.well2.inputs;
		}

		return [];
	}, [calculator, wellId]);

	const unitsDDItems: { [key: string]: IDDItem[] } = useMemo(() => {
		return inputs.reduce<Record<string, IDDItem[]>>((acc, input) => {
			const units = converter.getUnitsByCategory(input.unitCategoryId);
			acc[input.id] = units.map(unit => ({
				id: unit.id,
				translationKey: `measurement_units.${unit.category}.${unit.id}`
			} as IDDItem));
			return acc;
		}, {});

	}, [calculator, inputs]);

	const criteriaOptions = useMemo(() => {
		const well = calculator[wellId];
		if (!well) {
			return [];
		}
		return well.criteria.options.map(option => ({ id: option, translationKey: `vlp:criteria.${option}` } as IDDItem));
	}, [calculator, wellId]);

	const handleSelectCriteria = useCallback((selectedCriteria: string) => {
		if (!(selectedCriteria as VLPCriteria)) {
			return;
		}

		dispatch(setCriteria({ wellId: wellId, criteria: selectedCriteria as VLPCriteria }));
	}, [dispatch]);

	return <fieldset className="vlp__well-inputs">
		<legend>{wellId === "well1" ? t(`vlp:labels.well1_title`) : t(`vlp:labels.well2_title`)}</legend>

		{(wellId === "well2" || !secondWellOnScreen) && <button className="vlp__add-second-well-btn"
			title={wellId === "well1" ? t(`vlp:labels.add_well_title`) : t(`vlp:labels.collapse_well_title`)}
			onClick={toggleSecondWell}
		>{wellId === "well1" ? '+' : '−'}
		</button>}

		<div className={`vlp__criteria-${wellId}`}>
			<DropdownMenu
				inputId={calculator[wellId].criteria.id}
				items={criteriaOptions}
				selectedItemId={calculator[wellId].criteria.selected}
				onSelect={handleSelectCriteria}
			/>
		</div>

		{inputs.map((input) => (
			<InputRow
				key={`${wellId}-${input.id}`}
				inputId={input.id}
				dropdownItems={unitsDDItems[input.id]}
				onInputChange={onInputChange}
				onSelectUnit={onSelectUnit}
			/>
		))}
	</fieldset >;
};